import {
  capStackedDiscounts,
  evaluatePromotions,
  money,
  type AppliedPromotion,
  type EnginePromotion,
} from '@shared/pricing/promotion-engine';
import type { CartItem } from '@/types/pos';
import type { CachedLocation } from '@/lib/offline-db';
import type { TaxBreakdownLine } from '@/lib/tax';

/**
 * Prices a cart on the till with no connection (POS-I78), following the same steps as the server's
 * `SaleService.quoteSaleTotals` so the total printed and charged offline is the total the server
 * books when the sale syncs:
 *
 *  1. line value = unit price x quantity - line discount (already on the cart line);
 *  2. the order-level discount, then the automatic promotions (shared engine, stack-capped);
 *  3. GST, further tax (ATL) and embedded GST are charged only on the money the shop receives:
 *     every line's taxable value is scaled by (subtotal - order discount - promotions) / subtotal
 *     (POS-I54);
 *  4. total = goods - discounts + the EXCLUSIVE tax on top (inclusive GST is already in the price).
 *
 * What is NOT available offline (and therefore not priced): coupon codes, loyalty redemption,
 * gift cards, price tiers other than the one already applied to the cart line.
 */

export interface OfflineTaxRule {
  name: string;
  rate: number;
  type: 'INCLUSIVE' | 'EXCLUSIVE';
}

export interface OfflinePricingInput {
  items: CartItem[];
  /** Order-level discount amount already worked out by the cart. */
  orderDiscountAmount: number;
  /** Automatic promotions cached from the server. */
  promotions: EnginePromotion[];
  now?: Date;
  /** Further tax % to add on taxable lines; 0 when the buyer is on the ATL / the tenant has no ATL. */
  furtherTaxRate: number;
  /** Payment-method tax override (replaces the product taxes on lines that do not skip it). */
  pmTaxRule?: { name: string; rate: number } | null;
}

export interface OfflinePricing {
  subtotal: number;
  orderDiscountAmount: number;
  promotionDiscount: number;
  appliedPromotions: AppliedPromotion[];
  totalTax: number;
  exclusiveTax: number;
  furtherTaxAmount: number;
  taxBreakdown: TaxBreakdownLine[];
  /** Amount due before tip. */
  totalAmount: number;
}

// POS-I125: the server's rounding policy (backend `roundMoney`): every money figure that is shown,
// compared or stored is rounded to 2 dp, half up, ONCE (line value, each line tax, each total).
const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

function lineSubtotalOf(item: CartItem): number {
  return r2(r2(item.unitPrice * item.quantity) - item.discountAmount);
}

function taxesOf(item: CartItem, pmTaxRule: OfflinePricingInput['pmTaxRule']): OfflineTaxRule[] {
  const skipsPm = Boolean(item.product.skipPaymentMethodTax || item.product.category?.skipPaymentMethodTax);
  if (pmTaxRule && !skipsPm) {
    return [{ name: pmTaxRule.name, rate: pmTaxRule.rate, type: 'EXCLUSIVE' }];
  }
  return item.taxBreakdown.map((t) => ({ name: t.name, rate: Number(t.rate), type: t.type as 'INCLUSIVE' | 'EXCLUSIVE' }));
}

/** Promotions the till may apply right now: inside their start/end dates (the engine checks day, time and limits). */
export function promotionsRunningAt(promotions: EnginePromotion[], now: Date): EnginePromotion[] {
  return promotions.filter((p) => {
    if (p.startDate && new Date(p.startDate).getTime() > now.getTime()) return false;
    if (p.endDate && new Date(p.endDate).getTime() < now.getTime()) return false;
    return true;
  });
}

/**
 * The automatic promotions that apply to a cart offline: the shared engine on the cached promotions,
 * then the same stack cap the server applies after the order discount.
 */
export function previewOfflinePromotions(
  items: CartItem[],
  orderDiscountAmount: number,
  promotions: EnginePromotion[],
  now: Date = new Date(),
): { applied: AppliedPromotion[]; promotionDiscount: number } {
  const lines = items.map((item) => ({ item, lineSubtotal: lineSubtotalOf(item) }));
  const subtotal = r2(lines.reduce((sum, l) => sum + l.lineSubtotal, 0));
  const orderDiscount = Math.min(Math.max(0, orderDiscountAmount), Math.max(0, subtotal));
  const netOfOrderDiscount = subtotal - orderDiscount;

  const applied = capStackedDiscounts(
    evaluatePromotions(
      promotionsRunningAt(promotions, now),
      lines.map((l) => ({
        productId: l.item.product.id,
        categoryId: l.item.product.category?.id ?? null,
        quantity: l.item.quantity,
        lineSubtotal: l.lineSubtotal,
      })),
      netOfOrderDiscount,
      now,
    ),
    Math.max(0, netOfOrderDiscount),
  );
  const promotionDiscount = Math.min(
    money(applied.reduce((sum, p) => sum + p.discountAmount, 0)),
    Math.max(0, netOfOrderDiscount),
  );
  return { applied, promotionDiscount };
}

export function priceOfflineCart(input: OfflinePricingInput): OfflinePricing {
  const now = input.now ?? new Date();
  const lines = input.items.map((item) => ({ item, lineSubtotal: lineSubtotalOf(item) }));
  const subtotal = r2(lines.reduce((sum, l) => sum + l.lineSubtotal, 0));

  const orderDiscount = Math.min(r2(Math.max(0, input.orderDiscountAmount)), Math.max(0, subtotal));
  const { applied, promotionDiscount } = previewOfflinePromotions(
    input.items,
    input.orderDiscountAmount,
    input.promotions,
    now,
  );

  // POS-I54: tax only on what the shop receives.
  const taxDiscountTotal = orderDiscount + promotionDiscount;
  const taxBaseFactor =
    taxDiscountTotal > 0 && subtotal > 0 ? Math.max(0, subtotal - taxDiscountTotal) / subtotal : 1;

  let totalTax = 0;
  let inclusiveTax = 0;
  let furtherTaxAmount = 0;
  const byKey = new Map<string, TaxBreakdownLine>();
  const add = (name: string, rate: number, type: string, amount: number) => {
    const key = `${name}|${rate}|${type}`;
    const prev = byKey.get(key);
    const rounded = Number(amount.toFixed(2));
    if (prev) prev.amount = Number((prev.amount + rounded).toFixed(2));
    else byKey.set(key, { name, rate, type, amount: rounded } as TaxBreakdownLine);
  };

  for (const { item, lineSubtotal } of lines) {
    const taxable =
      taxBaseFactor === 1 ? lineSubtotal : (lineSubtotal * Math.max(0, subtotal - taxDiscountTotal)) / subtotal;
    const taxes = taxesOf(item, input.pmTaxRule);
    for (const tax of taxes) {
      const amount = r2(
        tax.type === 'INCLUSIVE' ? taxable - taxable / (tax.rate / 100 + 1) : (taxable * tax.rate) / 100,
      );
      add(tax.name, tax.rate, tax.type, amount);
      totalTax += amount;
      if (tax.type === 'INCLUSIVE') inclusiveTax += amount;
    }
    // POS-I53: further tax only on lines that carry sales tax; none under a payment-method override.
    const carriesSalesTax = taxes.some((t) => t.rate > 0);
    if (input.furtherTaxRate > 0 && !input.pmTaxRule && carriesSalesTax) {
      const amount = r2((taxable * input.furtherTaxRate) / 100);
      add(`Further Tax (${input.furtherTaxRate}%)`, input.furtherTaxRate, 'EXCLUSIVE', amount);
      totalTax += amount;
      furtherTaxAmount += amount;
    }
  }

  const netMerchandise = r2(subtotal - orderDiscount - promotionDiscount);
  const exclusiveTax = Math.max(0, r2(totalTax - inclusiveTax));
  const totalAmount = r2(netMerchandise + exclusiveTax);

  return {
    subtotal: money(subtotal),
    orderDiscountAmount: money(orderDiscount),
    promotionDiscount: money(promotionDiscount),
    appliedPromotions: applied,
    totalTax: money(totalTax),
    exclusiveTax: money(exclusiveTax),
    furtherTaxAmount: money(furtherTaxAmount),
    taxBreakdown: [...byKey.values()],
    totalAmount: money(totalAmount),
  };
}

export interface OfflineAtlConfig {
  atlEnabled: boolean;
  furtherTaxRate: number;
  defaultBuyerIsOnAtl: boolean;
}

/**
 * Further-tax settings exactly as the server resolves them: a location with ATL enabled uses its
 * own rate and default buyer status, otherwise the tenant's; ATL is on when either has it on.
 */
export function resolveOfflineAtl(
  location: Pick<CachedLocation, 'atlEnabled' | 'defaultBuyerAtlStatus' | 'furtherTaxRate'> | null | undefined,
  tenant: { atlEnabled?: boolean; defaultBuyerAtlStatus?: boolean; furtherTaxRate?: number } | null | undefined,
): OfflineAtlConfig {
  const locationAtl = Boolean(location?.atlEnabled);
  return {
    atlEnabled: locationAtl || Boolean(tenant?.atlEnabled),
    furtherTaxRate: locationAtl ? Number(location?.furtherTaxRate ?? 0) : Number(tenant?.furtherTaxRate ?? 0),
    defaultBuyerIsOnAtl: locationAtl
      ? Boolean(location?.defaultBuyerAtlStatus ?? false)
      : Boolean(tenant?.defaultBuyerAtlStatus ?? false),
  };
}

/** Further tax % to apply to a sale, or 0 when it does not apply (buyer on the ATL, or no ATL). */
export function offlineFurtherTaxRate(atl: OfflineAtlConfig, buyerIsOnAtl: boolean): number {
  return atl.atlEnabled && !buyerIsOnAtl && atl.furtherTaxRate > 0 ? atl.furtherTaxRate : 0;
}
